import React, {useState, useCallback, useMemo} from 'react';
import {
    Box, Stack, TextField, Typography, Switch, FormControlLabel,
    ToggleButtonGroup, ToggleButton, Alert, IconButton, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Divider
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CodeIcon from '@mui/icons-material/Code';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import {JsonEditor as JsonEditReact} from 'json-edit-react';
import EntityCriteriaEditor from './EntityCriteriaEditor';

const DEFAULTS = {
    version: 'v1',
    number_range: {min: 1, max: 99},
    rewards: {correct_base: 100, wrong_consolation: 0, milestones: []},
    streak: {max_multiplier: null},
    session: {ttl_seconds: 300, idle_timeout_seconds: 120, max_continues_per_session: null},
    iap_continue_product_id: 'hilo_gelt_1',
};

const ensureConfig = (config) => ({
    version: config?.version ?? DEFAULTS.version,
    number_range: {
        min: config?.number_range?.min ?? DEFAULTS.number_range.min,
        max: config?.number_range?.max ?? DEFAULTS.number_range.max,
    },
    rewards: {
        correct_base: config?.rewards?.correct_base ?? DEFAULTS.rewards.correct_base,
        wrong_consolation: config?.rewards?.wrong_consolation ?? DEFAULTS.rewards.wrong_consolation,
        milestones: config?.rewards?.milestones ?? [],
    },
    streak: {
        max_multiplier: config?.streak?.max_multiplier ?? null,
    },
    session: {
        ttl_seconds: config?.session?.ttl_seconds ?? DEFAULTS.session.ttl_seconds,
        idle_timeout_seconds: config?.session?.idle_timeout_seconds ?? DEFAULTS.session.idle_timeout_seconds,
        max_continues_per_session: config?.session?.max_continues_per_session ?? null,
    },
    iap_continue_product_id: config?.iap_continue_product_id ?? DEFAULTS.iap_continue_product_id,
});

const MultiplierPreview = ({maxMultiplier}) => {
    const rows = useMemo(() => {
        const result = [];
        const limit = maxMultiplier != null ? maxMultiplier : 5;
        for (let m = 1; m <= limit; m++) {
            const lo = (m - 1) * 3;
            const hi = m === limit && maxMultiplier == null ? '+' : m * 3 - 1;
            result.push({streak: `${lo}${hi === '+' ? '+' : `-${hi}`}`, multiplier: `x${m}`});
        }
        return result;
    }, [maxMultiplier]);

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Streak</TableCell>
                        <TableCell>Multiplier</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow key={r.streak}>
                            <TableCell>{r.streak}</TableCell>
                            <TableCell>{r.multiplier}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

function HiLoConfigEditor({value = {}, onChange, disabled = false}) {
    const [mode, setMode] = useState('form');
    const [textValue, setTextValue] = useState(() => JSON.stringify(value, null, 2));
    const [textError, setTextError] = useState(null);
    const [switchError, setSwitchError] = useState(null);

    const config = ensureConfig(value);

    const update = useCallback((path, val) => {
        const next = JSON.parse(JSON.stringify(value || {}));
        const keys = path.split('.');
        let obj = next;
        for (let i = 0; i < keys.length - 1; i++) {
            if (obj[keys[i]] == null) obj[keys[i]] = {};
            obj = obj[keys[i]];
        }
        obj[keys[keys.length - 1]] = val;
        onChange(next);
    }, [value, onChange]);

    const handleModeChange = useCallback((_, newMode) => {
        if (!newMode) return;
        setSwitchError(null);

        if (mode === 'text') {
            try {
                const parsed = JSON.parse(textValue);
                onChange(parsed);
                setTextError(null);
            } catch {
                setSwitchError('Fix JSON errors before switching');
                return;
            }
        }
        if (newMode === 'text') {
            setTextValue(JSON.stringify(value, null, 2));
            setTextError(null);
        }
        setMode(newMode);
    }, [value, textValue, mode, onChange]);

    const handleTextChange = useCallback((e) => {
        const raw = e.target.value;
        setTextValue(raw);
        try {
            const parsed = JSON.parse(raw);
            setTextError(null);
            onChange(parsed);
        } catch {
            setTextError('Invalid JSON');
        }
    }, [onChange]);

    // Milestone CRUD
    const milestones = config.rewards.milestones;

    const addMilestone = () => {
        const last = milestones[milestones.length - 1];
        const newThreshold = last ? last.threshold + 100 : 100;
        update('rewards.milestones', [
            ...milestones,
            {threshold: newThreshold, reward: [{action: 'increase', entity: 'currency', criteria: {identifier: '1', amount: 500}}]},
        ]);
    };

    const removeMilestone = (index) => {
        update('rewards.milestones', milestones.filter((_, i) => i !== index));
    };

    const moveMilestone = (index, dir) => {
        const next = [...milestones];
        const swapIdx = index + dir;
        if (swapIdx < 0 || swapIdx >= next.length) return;
        [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
        update('rewards.milestones', next);
    };

    const updateMilestoneThreshold = (index, val) => {
        const next = [...milestones];
        next[index] = {...next[index], threshold: val};
        update('rewards.milestones', next);
    };

    const updateMilestoneReward = (index, reward) => {
        const next = [...milestones];
        next[index] = {...next[index], reward};
        update('rewards.milestones', next);
    };

    const renderForm = () => (
        <Stack spacing={3}>
            {/* Number Range */}
            <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Number Range</Typography>
                <Stack direction="row" spacing={2}>
                    <TextField size="small" type="number" label="Min" sx={{width: 120}}
                               value={config.number_range.min}
                               onChange={e => update('number_range.min', parseInt(e.target.value) || 0)}
                               disabled={disabled} slotProps={{htmlInput: {min: 1}}}/>
                    <TextField size="small" type="number" label="Max" sx={{width: 120}}
                               value={config.number_range.max}
                               onChange={e => update('number_range.max', parseInt(e.target.value) || 0)}
                               disabled={disabled} slotProps={{htmlInput: {min: 2}}}/>
                </Stack>
            </Box>

            <Divider/>

            {/* Rewards */}
            <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Rewards</Typography>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField size="small" type="number" label="Base Reward per Correct Guess" sx={{width: 260}}
                                   value={config.rewards.correct_base}
                                   onChange={e => update('rewards.correct_base', parseInt(e.target.value) || 0)}
                                   disabled={disabled} slotProps={{htmlInput: {min: 0}}}/>
                        <TextField size="small" type="number" label="Wrong Consolation" sx={{width: 180}}
                                   value={config.rewards.wrong_consolation}
                                   onChange={e => update('rewards.wrong_consolation', parseInt(e.target.value) || 0)}
                                   disabled={disabled} slotProps={{htmlInput: {min: 0}}}/>
                    </Stack>

                    <Box>
                        <Typography variant="subtitle2" sx={{mb: 1}}>Milestones</Typography>
                        {milestones.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                No milestones configured.
                            </Typography>
                        )}
                        {milestones.map((ms, i) => (
                            <Paper key={i} variant="outlined" sx={{p: 1.5, mb: 1}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                                    <Typography variant="body2" sx={{fontWeight: 'bold', minWidth: 20}}>
                                        #{i + 1}
                                    </Typography>
                                    <TextField size="small" type="number" label="Threshold" sx={{width: 120}}
                                               value={ms.threshold}
                                               onChange={e => updateMilestoneThreshold(i, parseInt(e.target.value) || 0)}
                                               disabled={disabled} slotProps={{htmlInput: {min: 1}}}/>
                                    <Box sx={{flex: 1}}/>
                                    <IconButton size="small" onClick={() => moveMilestone(i, -1)}
                                                disabled={disabled || i === 0}><ArrowUpwardIcon
                                        fontSize="small"/></IconButton>
                                    <IconButton size="small" onClick={() => moveMilestone(i, 1)}
                                                disabled={disabled || i === milestones.length - 1}><ArrowDownwardIcon
                                        fontSize="small"/></IconButton>
                                    <IconButton size="small" color="error" onClick={() => removeMilestone(i)}
                                                disabled={disabled}><DeleteIcon fontSize="small"/></IconButton>
                                </Stack>
                                <EntityCriteriaEditor
                                    value={ms.reward || []}
                                    onChange={reward => updateMilestoneReward(i, reward)}
                                    disabled={disabled}
                                    preset="reward"
                                />
                            </Paper>
                        ))}
                        <Button startIcon={<AddIcon/>} onClick={addMilestone} disabled={disabled} size="small">
                            Add Milestone
                        </Button>
                    </Box>
                </Stack>
            </Box>

            <Divider/>

            {/* Streak Multiplier */}
            <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Streak Multiplier</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Formula: floor(streak / 3) + 1
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 1}}>
                    <TextField size="small" type="number" label="Max Multiplier" sx={{width: 160}}
                               value={config.streak.max_multiplier ?? ''}
                               onChange={e => update('streak.max_multiplier', e.target.value === '' ? null : parseInt(e.target.value) || 1)}
                               disabled={disabled || config.streak.max_multiplier == null}
                               slotProps={{htmlInput: {min: 1}}}/>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.streak.max_multiplier == null}
                                onChange={e => update('streak.max_multiplier', e.target.checked ? null : 5)}
                                disabled={disabled}
                                size="small"
                            />
                        }
                        label="Unlimited"
                    />
                </Stack>
                <MultiplierPreview maxMultiplier={config.streak.max_multiplier}/>
            </Box>

            <Divider/>

            {/* Session */}
            <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Session</Typography>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField size="small" type="number" label="TTL (seconds)" sx={{width: 160}}
                                   value={config.session.ttl_seconds}
                                   onChange={e => update('session.ttl_seconds', parseInt(e.target.value) || 0)}
                                   disabled={disabled} slotProps={{htmlInput: {min: 1}}}/>
                        <TextField size="small" type="number" label="Idle Timeout (seconds)" sx={{width: 200}}
                                   value={config.session.idle_timeout_seconds}
                                   onChange={e => update('session.idle_timeout_seconds', parseInt(e.target.value) || 0)}
                                   disabled={disabled} slotProps={{htmlInput: {min: 1}}}/>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField size="small" type="number" label="Max Continues" sx={{width: 160}}
                                   value={config.session.max_continues_per_session ?? ''}
                                   onChange={e => update('session.max_continues_per_session', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                                   disabled={disabled || config.session.max_continues_per_session == null}
                                   slotProps={{htmlInput: {min: 0}}}/>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.session.max_continues_per_session == null}
                                    onChange={e => update('session.max_continues_per_session', e.target.checked ? null : 3)}
                                    disabled={disabled}
                                    size="small"
                                />
                            }
                            label="Unlimited"
                        />
                    </Stack>
                    <TextField size="small" label="IAP Continue Product ID" sx={{width: 260}}
                               value={config.iap_continue_product_id}
                               onChange={e => update('iap_continue_product_id', e.target.value)}
                               disabled={disabled}/>
                </Stack>
            </Box>

            <Divider/>

            {/* Version */}
            <TextField size="small" label="Version" value={config.version} disabled sx={{width: 120}}/>
        </Stack>
    );

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}}>
                <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                    <ToggleButton value="form"><ViewListIcon sx={{mr: 0.5}}/> Form</ToggleButton>
                    <ToggleButton value="tree"><AccountTreeIcon sx={{mr: 0.5}}/> Tree</ToggleButton>
                    <ToggleButton value="text"><CodeIcon sx={{mr: 0.5}}/> Text</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {switchError && <Alert severity="warning" sx={{mb: 1.5}}>{switchError}</Alert>}

            {mode === 'form' && renderForm()}

            {mode === 'tree' && (
                <Box sx={{'& .jer-editor-container': {fontFamily: 'monospace', fontSize: '0.875rem'}}}>
                    <JsonEditReact
                        data={value}
                        setData={onChange}
                        rootName="config"
                        collapse={2}
                        restrictEdit={disabled}
                        restrictDelete={disabled}
                        restrictAdd={disabled}
                        restrictTypeSelection={disabled}
                        minWidth="100%"
                    />
                </Box>
            )}

            {mode === 'text' && (
                <>
                    {textError && <Alert severity="error" sx={{mb: 1}}>{textError}</Alert>}
                    <TextField
                        fullWidth multiline minRows={6} maxRows={30}
                        value={textValue}
                        onChange={handleTextChange}
                        disabled={disabled}
                        error={!!textError}
                        slotProps={{input: {sx: {fontFamily: 'monospace', fontSize: '0.875rem'}}}}
                    />
                </>
            )}
        </Box>
    );
}

HiLoConfigEditor.validate = (config) => {
    if (!config) return 'Config is required';
    if (config.number_range?.min >= config.number_range?.max) return 'Min must be less than max';
    if (config.number_range?.min < 1) return 'Min must be >= 1';
    if (config.rewards?.correct_base == null) return 'Base reward is required';
    const milestones = config.rewards?.milestones || [];
    for (let i = 1; i < milestones.length; i++) {
        if (milestones[i].threshold <= milestones[i - 1].threshold)
            return `Milestone ${i + 1} threshold must be greater than milestone ${i}`;
    }
    return null;
};

export default HiLoConfigEditor;
